import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Calendar, MapPin, Clock, Share2, ArrowRight, Loader2, AlertCircle
} from 'lucide-react';
import { ReservationDetail } from '../types';
import { reservationApi } from '../services/api';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { formatDateRange } from '../utils/fairStatus'; // Issue #1672

export default function ReservationConfirmation() {
  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get('id');

  const [reservation, setReservation] = useState<ReservationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadReservation = async () => {
      if (!reservationId) {
        setError('No reservation ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await reservationApi.getReservationDetails(parseInt(reservationId, 10));
        if (isMounted) {
          setReservation(data);
        }
      } catch (err: unknown) {
        if (isMounted) {
          setError(err.response?.data?.detail || 'Failed to load reservation');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadReservation();

    return () => {
      isMounted = false;
    };
  }, [reservationId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12 bg-red-50 rounded-lg">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error || 'Reservation not found'}</h2>
          <Link to="/reservations" className="text-secondary hover:text-primary-dark font-medium">
            View My Reservations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-12 h-12 text-secondary" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Reservation Submitted!</h1>
        <p className="text-gray-600">
          Your reservation request has been received and is pending approval.
        </p>
      </div>

      {/* Confirmation Number */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 text-center">
        <p className="text-sm text-primary-dark mb-1">Confirmation Number</p>
        <p className="text-2xl font-bold text-primary-dark">RES-{reservation.id.toString().padStart(6, '0')}</p>
      </div>

      {/* Reservation Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Reservation Details</h2>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">{reservation.fair_name}</p>
              {reservation.fair_city && reservation.fair_state && (
                <p className="text-sm text-gray-600">
                  {reservation.fair_city}, {reservation.fair_state}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">
                {reservation.reservation_type === 'full_duration' ? 'Full Duration' : 'Selected Dates'}
              </p>
              {reservation.fair_start_date && reservation.fair_end_date && (
                <p className="text-sm text-gray-600">
                  {formatDateRange(reservation.fair_start_date, reservation.fair_end_date)}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Slot #{reservation.slot_number}</p>
              {reservation.slot_dimensions && (
                <p className="text-sm text-gray-600">{reservation.slot_dimensions}</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 mt-4 pt-4">
          <div className="flex justify-between text-lg">
            <span className="font-medium text-gray-900">Total Price</span>
            <span className="font-bold text-secondary">{formatCurrency(reservation.price_paid)} {/* Issue #905: Decimal.js formatting */}</span>
          </div>
        </div>
      </div>

      {/* Status Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Pending Approval</p>
            <p className="text-sm text-yellow-700">
              The fair manager will review your reservation. You'll receive a notification once it's confirmed.
            </p>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">What's Next?</h2>
        <ul className="space-y-3 text-gray-600">
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-teal-100 text-secondary rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">1</span>
            <span>Wait for the fair manager to review and confirm your reservation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-teal-100 text-secondary rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">2</span>
            <span>Once confirmed, you'll receive setup instructions via email</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 bg-teal-100 text-secondary rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">3</span>
            <span>Arrive at the venue on the scheduled date with your products</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Link
          to="/reservations"
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium"
        >
          View My Reservations
          <ArrowRight className="w-4 h-4" />
        </Link>
        <button
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: 'My Reservation',
                text: `I've reserved a slot at ${reservation.fair_name}!`,
              });
            }
          }}
          className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </div>
    </div>
  );
}
